import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import "../styles/GameBoard.css";
import { CardSection } from "./CardSection/CardSection.js";
import { BonusGem, CostRow } from "./CardSection/Card.js";
import PlayerPanel from "./PlayerPanel/PlayerPanel.js";
import PurchaseModal from "./PurchaseModal/PurchaseModal.js";
import ReserveModal from "./ReserveModal/ReserveModal.js";
import GameLog from "./GameLog/GameLog.js";
import { getCardById } from "../data/cardData.js";

const GEM_COLORS = ["WHITE", "BLUE", "GREEN", "RED", "BLACK"];
const GEM_ALL = [...GEM_COLORS, "GOLD"];
const COLOR_LABEL = { WHITE: "白", BLUE: "蓝", GREEN: "绿", RED: "红", BLACK: "黑", GOLD: "金" };
const TOKEN_IMG = {
  WHITE: '/cards/tokens/white.png', BLUE: '/cards/tokens/blue.png',
  GREEN: '/cards/tokens/green.png', RED: '/cards/tokens/red.png',
  BLACK: '/cards/tokens/black.png', GOLD: '/cards/tokens/gold.png',
};
const tokenStyle = (color) => ({ backgroundImage: `url(${process.env.PUBLIC_URL}${TOKEN_IMG[color]})`, backgroundSize: 'cover', backgroundPosition: 'center' });

function GameBoard({ gameMeta, gameState, currentUserId, onAction, onChooseNoble, onLeave, actionError }) {
  const [selectedTokens, setSelectedTokens] = useState({});
  const [returnTokens, setReturnTokens] = useState({});
  const [returnMode, setReturnMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const [purchaseCard, setPurchaseCard] = useState(null);
  const [reserveInfo, setReserveInfo] = useState(null);
  const [flyAnim, setFlyAnim] = useState(null);
  const prevLogLen = useRef(0);
  const logInitialized = useRef(false);

  // Derived
  const currentPlayerId = gameState.turn_order[gameState.current_player_index];
  const isMyTurn = currentPlayerId === currentUserId;
  const myState = gameState.players[currentUserId] || null;
  const totalSelected = Object.values(selectedTokens).reduce((s, v) => s + v, 0);
  const pendingNobles = gameState.pending_noble_choice || [];
  const isFinished = gameMeta.status === "finished";

  const userMap = useMemo(() => {
    const m = {};
    (gameMeta.players || []).forEach((p) => { m[p.user] = p.username; });
    return m;
  }, [gameMeta.players]);

  const winner = useMemo(() => {
    if (!isFinished) return null;
    let best = null;
    for (const [pid, ps] of Object.entries(gameState.players)) {
      if (!best || ps.prestige_points > best.pts ||
          (ps.prestige_points === best.pts && ps.purchased_cards.length < best.cards)) {
        best = { id: Number(pid), pts: ps.prestige_points, cards: ps.purchased_cards.length };
      }
    }
    return best;
  }, [isFinished, gameState.players]);

  const myTokenTotal = myState ? Object.values(myState.tokens).reduce((s, v) => s + v, 0) : 0;

  const clearTokenDraft = useCallback(() => {
    setSelectedTokens({});
    setReturnTokens({});
    setReturnMode(null);
    setLocalError("");
  }, []);

  useEffect(() => {
    clearTokenDraft();
  }, [currentPlayerId, clearTokenDraft]);

  // ===== Token Selection =====
  const handleTokenClick = (color) => {
    if (!isMyTurn || isFinished || pendingNobles.length > 0 || returnMode || busy) return;
    if (color === "GOLD") return;
    if (gameState.bank[color] <= 0) return;

    const current = selectedTokens[color] || 0;
    const hasTwoSame = Object.values(selectedTokens).some((v) => v === 2);

    if (current === 0) {
      if (hasTwoSame || totalSelected >= 3) return;
      setSelectedTokens((prev) => ({ ...prev, [color]: 1 }));
    } else if (current === 1) {
      const othersCount = totalSelected - 1;
      if (othersCount > 0) return;
      if (gameState.bank[color] < 4) {
        setLocalError(`银行${COLOR_LABEL[color]}宝石不足4个，不能拿2个`);
        return;
      }
      setSelectedTokens((prev) => ({ ...prev, [color]: 2 }));
    }
    setLocalError("");
  };

  const handleTokenRightClick = (e, color) => {
    e.preventDefault();
    const current = selectedTokens[color] || 0;
    if (current > 0) {
      setSelectedTokens((prev) => {
        const next = { ...prev, [color]: current - 1 };
        if (next[color] === 0) delete next[color];
        return next;
      });
    }
  };

  const getSelectionHint = () => {
    if (totalSelected === 0) return "点击宝石选择（左键加，右键减）";
    const entries = Object.entries(selectedTokens).filter(([, v]) => v > 0);
    if (entries.some(([, v]) => v === 2) && entries.length === 1) return "✓ 拿取2个同色宝石";
    if (entries.length === 3 && totalSelected === 3) return "✓ 拿取3种不同宝石";
    return `继续选择（已选 ${totalSelected}）`;
  };

  const canTake = () => {
    const entries = Object.entries(selectedTokens).filter(([, v]) => v > 0);
    const total = entries.reduce((s, [, v]) => s + v, 0);
    if (entries.length === 1 && entries[0][1] === 2) return true;
    if (entries.length === 3 && total === 3 && entries.every(([, v]) => v === 1)) return true;
    return false;
  };

  const handleTakeTokens = async () => {
    if (busy || !canTake()) return;
    const colors = [];
    Object.entries(selectedTokens).forEach(([c, n]) => { for (let i = 0; i < n; i++) colors.push(c); });

    const afterTotal = myTokenTotal + colors.length;
    if (afterTotal > 10) {
      setReturnMode({ type: "take", payload: { colors }, needed: afterTotal - 10, extraTokens: colors });
      setReturnTokens({});
      return;
    }

    setBusy(true);
    try { await onAction("take_tokens", { colors }); setSelectedTokens({}); } catch {}
    finally { setBusy(false); }
  };

  // ===== Return Tokens =====
  const totalReturn = Object.values(returnTokens).reduce((s, v) => s + v, 0);

  const getTokensAfterAction = () => {
    if (!myState) return {};
    const tokens = { ...myState.tokens };
    if (returnMode?.extraTokens) {
      returnMode.extraTokens.forEach((c) => { tokens[c] = (tokens[c] || 0) + 1; });
    } else if (returnMode?.type === "reserve" && gameState.bank.GOLD > 0) {
      tokens.GOLD = (tokens.GOLD || 0) + 1;
    }
    return tokens;
  };

  const handleReturnClick = (color) => {
    if (!returnMode || totalReturn >= returnMode.needed) return;
    const tokensAfter = getTokensAfterAction();
    const available = (tokensAfter[color] || 0) - (returnTokens[color] || 0);
    if (available <= 0) return;
    setReturnTokens((prev) => ({ ...prev, [color]: (prev[color] || 0) + 1 }));
  };

  const handleReturnRightClick = (e, color) => {
    e.preventDefault();
    if ((returnTokens[color] || 0) > 0) {
      setReturnTokens((prev) => ({ ...prev, [color]: prev[color] - 1 }));
    }
  };

  const handleConfirmReturn = async () => {
    if (totalReturn !== returnMode.needed || busy) return;
    setBusy(true);
    try {
      if (returnMode.type === "take") {
        const rt = {};
        for (const [c, v] of Object.entries(returnTokens)) { if (v > 0) rt[c] = v; }
        await onAction("take_tokens", { ...returnMode.payload, return_tokens: rt });
        setSelectedTokens({});
      } else if (returnMode.type === "reserve") {
        const returnColor = Object.entries(returnTokens).find(([, v]) => v > 0)?.[0];
        await onAction("reserve_card", { ...returnMode.payload, return_color: returnColor });
      }
      setReturnMode(null);
      setReturnTokens({});
    } catch {}
    finally { setBusy(false); }
  };

  const handleCancelReturn = () => { setReturnMode(null); setReturnTokens({}); setSelectedTokens({}); };

  // ===== Card Actions =====
  const handleBuyCard = (card) => {
    if (busy || !isMyTurn) return;
    clearTokenDraft();
    // Open purchase modal instead of buying directly
    setPurchaseCard(card);
  };

  const handleConfirmPurchase = async (payment) => {
    if (busy || !purchaseCard) return;
    setBusy(true);
    try {
      await onAction("buy_card", { card_id: purchaseCard.id, payment });
      setPurchaseCard(null);
      clearTokenDraft();
    } catch {}
    finally { setBusy(false); }
  };

  const handleCancelPurchase = () => {
    setPurchaseCard(null);
  };

  const handleReserveCard = (card) => {
    if (busy || !isMyTurn) return;
    clearTokenDraft();
    setReserveInfo({ card });
  };

  const handleBlindReserve = (tier) => {
    if (busy || !isMyTurn) return;
    clearTokenDraft();
    setReserveInfo({ tier });
  };

  const handleConfirmReserve = async () => {
    if (busy || !reserveInfo) return;
    const card = reserveInfo.card;
    const tier = reserveInfo.tier;
    if (gameState.bank.GOLD > 0 && myTokenTotal >= 10) {
      const payload = card ? { card_id: card.id } : { tier };
      setReturnMode({ type: "reserve", payload, needed: 1 });
      setReturnTokens({});
      setReserveInfo(null);
      return;
    }
    setBusy(true);
    try {
      if (card) {
        await onAction("reserve_card", { card_id: card.id });
      } else {
        await onAction("reserve_card", { tier });
      }
      setReserveInfo(null);
      clearTokenDraft();
    } catch {}
    finally { setBusy(false); }
  };

  const handleCancelReserve = () => { setReserveInfo(null); };

  // ===== Noble Choice =====
  const handleNobleChoice = async (nobleId) => {
    if (busy) return;
    setBusy(true);
    try { await onChooseNoble(nobleId); clearTokenDraft(); } catch {}
    finally { setBusy(false); }
  };

  // --- Card fly animation ---
  const triggerFlyAnimation = useCallback((card, targetPid) => {
    const gameMain = document.querySelector('.game-main');
    const playerEl = document.querySelector(`[data-player-id="${targetPid}"]`);
    if (!gameMain || !playerEl) return;
    const sr = gameMain.getBoundingClientRect();
    const er = playerEl.getBoundingClientRect();
    setFlyAnim({
      card, key: Date.now(),
      startX: sr.left + sr.width / 2 - 48,
      startY: sr.top + sr.height / 3,
      endX: er.left + 20,
      endY: er.top + 40,
    });
    setTimeout(() => setFlyAnim(null), 700);
  }, []);

  useEffect(() => {
    const log = gameState?.log;
    if (!log) return;
    if (!logInitialized.current) {
      prevLogLen.current = log.length;
      logInitialized.current = true;
      return;
    }
    if (log.length > prevLogLen.current) {
      for (let i = prevLogLen.current; i < log.length; i++) {
        const entry = log[i];
        if ((entry.action === 'buy_card' || entry.action === 'reserve_card') && entry.card) {
          const fullCard = getCardById(entry.card.id);
          if (fullCard) { triggerFlyAnimation(fullCard, entry.player_id); break; }
        }
      }
      prevLogLen.current = log.length;
    }
  }, [gameState?.log, triggerFlyAnimation]);

  const interactable = isMyTurn && !isFinished && pendingNobles.length === 0 && !returnMode && !purchaseCard && !reserveInfo;
  const error = localError || actionError;

  return (
    <div className="game-board">
      {/* Winner overlay */}
      {isFinished && winner && (
        <div className="modal-overlay">
          <div className="modal-content winner-modal">
            <h2>🏆 游戏结束！</h2>
            <p className="winner-name">{userMap[winner.id] || `Player ${winner.id}`} 获胜！</p>
            <p>最终得分：{winner.pts} 分</p>
            <button className="btn-primary" onClick={onLeave}>返回大厅</button>
          </div>
        </div>
      )}

      {/* Noble choice modal */}
      {pendingNobles.length > 0 && currentPlayerId === currentUserId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>选择一位贵族</h3>
            <div className="noble-choices">
              {pendingNobles.map((nid) => {
                const noble = gameState.nobles_on_table.find((n) => n.id === nid);
                if (!noble) return null;
                return (
                  <div key={nid} className="noble-card selectable" onClick={() => handleNobleChoice(nid)}>
                    <div className="noble-points">{noble.points}</div>
                    <div className="noble-requirements">
                      {Object.entries(noble.requirement).map(([c, n]) => (
                        <span key={c} className={`token-dot ${c.toLowerCase()}`} style={tokenStyle(c)}>{n}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Purchase modal */}
      {purchaseCard && myState && (
        <PurchaseModal
          card={purchaseCard}
          playerState={myState}
          onConfirm={handleConfirmPurchase}
          onCancel={handleCancelPurchase}
          busy={busy}
        />
      )}

      {/* Reserve modal */}
      {reserveInfo && (
        <ReserveModal
          reserveInfo={reserveInfo}
          bankGold={gameState.bank.GOLD || 0}
          tokenTotal={myTokenTotal}
          onConfirm={handleConfirmReserve}
          onCancel={handleCancelReserve}
          busy={busy}
        />
      )}

      {/* Return token modal */}
      {returnMode && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>归还 {returnMode.needed} 个宝石</h3>
            <p className="return-hint">已选：{totalReturn} / {returnMode.needed}（左键加，右键减）</p>
            <div className="return-tokens-grid">
              {GEM_ALL.map((color) => {
                const after = getTokensAfterAction()[color] || 0;
                if (after === 0) return null;
                const sel = returnTokens[color] || 0;
                return (
                  <div key={color}
                    className={`token ${color.toLowerCase()} ${sel > 0 ? "selected" : ""}`}
                    style={tokenStyle(color)}
                    onClick={() => handleReturnClick(color)}
                    onContextMenu={(e) => handleReturnRightClick(e, color)}
                  >
                    <span className="token-label">{COLOR_LABEL[color]}</span>
                    <span className="token-count">{after}</span>
                    {sel > 0 && <span className="selected-count">还{sel}</span>}
                  </div>
                );
              })}
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleConfirmReturn}
                disabled={totalReturn !== returnMode.needed || busy}>确认归还</button>
              <button className="btn-secondary" onClick={handleCancelReturn}>取消</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="game-header game-topbar">
        <button className="btn-secondary btn-back" onClick={onLeave}>← 大厅</button>
        <div className="game-info">
          <span>房间 #{gameMeta.id}</span>
          <span className="divider">|</span>
          <span>回合 {gameState.round_number}</span>
          <span className="divider">|</span>
          <span>当前：<strong>{userMap[currentPlayerId] || "?"}</strong></span>
        </div>
        {isMyTurn && !isFinished && <span className="turn-badge">轮到你了！</span>}
      </header>

      {error && <p className="game-error">{error}</p>}

      <main className="game-stage">
        <section className="game-table">
          <div className="game-main table-grid">
            {/* Nobles */}
            <section className="nobles-section">
              <h3>贵族</h3>
              <div className="nobles-container">
                {gameState.nobles_on_table.map((noble) => (
                  <div key={noble.id} className="noble-card">
                    <div className="noble-points">{noble.points}</div>
                    <div className="noble-requirements">
                      {Object.entries(noble.requirement).map(([color, count]) => (
                        <span key={color} className={`token-dot ${color.toLowerCase()}`} style={tokenStyle(color)}>{count}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Cards */}
            <CardSection
              gameState={gameState}
              myPlayerState={myState}
              isMyTurn={interactable}
              onBuyCard={handleBuyCard}
              onReserveCard={handleReserveCard}
              onBlindReserve={handleBlindReserve}
            />

            {/* Bank Tokens */}
            <section className="tokens-section">
              <h3>银行宝石</h3>
              <div className="tokens-container">
                {GEM_ALL.map((color) => (
                  <div key={color}
                    className={`token ${color.toLowerCase()}${
                      color !== "GOLD" && interactable ? " clickable" : ""
                    }${(selectedTokens[color] || 0) > 0 ? " selected" : ""}`}
                    style={tokenStyle(color)}
                    onClick={() => handleTokenClick(color)}
                    onContextMenu={(e) => handleTokenRightClick(e, color)}
                    title={color === "GOLD" ? "金币不能直接拿取" : `${COLOR_LABEL[color]} (${gameState.bank[color]})`}
                  >
                    <span className="token-label">{COLOR_LABEL[color]}</span>
                    <span className="token-count">{gameState.bank[color]}</span>
                    {(selectedTokens[color] || 0) > 0 && (
                      <span className="selected-count">+{selectedTokens[color]}</span>
                    )}
                  </div>
                ))}
              </div>
              {interactable && (
                <div className="token-actions">
                  <span className="selection-hint">{getSelectionHint()}</span>
                  <button className="take-tokens-btn" onClick={handleTakeTokens}
                    disabled={!canTake() || busy}>
                    拿取宝石
                  </button>
                  {totalSelected > 0 && (
                    <button className="btn-secondary btn-clear"
                      onClick={() => { setSelectedTokens({}); setLocalError(""); }}>清除</button>
                  )}
                </div>
              )}
            </section>
          </div>
        </section>

        {/* Players sidebar */}
        <PlayerPanel
          gameState={gameState}
          currentUserId={currentUserId}
          userMap={userMap}
          isMyTurn={isMyTurn}
          interactable={interactable}
          onBuyCard={handleBuyCard}
        />
      </main>

      {/* Game Log */}
      <GameLog log={gameState.log} userMap={userMap} />

      {/* Card fly animation */}
      {flyAnim && (
        <div className="fly-card-layer" key={flyAnim.key}>
          <div
            className={`fly-card dev-card ${flyAnim.card.color.toLowerCase()}`}
            style={{
              '--fly-sx': `${flyAnim.startX}px`,
              '--fly-sy': `${flyAnim.startY}px`,
              '--fly-ex': `${flyAnim.endX}px`,
              '--fly-ey': `${flyAnim.endY}px`,
            }}
          >
            <div className="dev-card__art">
              <span className="sparkle" /><span className="sparkle" /><span className="sparkle" />
            </div>
            <div className="dev-card__info">
              <div className="dev-card__header">
                <span className="dev-card__points">{flyAnim.card.points || ''}</span>
                <BonusGem color={flyAnim.card.color} />
              </div>
              <CostRow cost={flyAnim.card.cost} className="dev-card__cost" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GameBoard;
